import { openDB, type IDBPDatabase } from 'idb';
import { DATABASE_CONSTANTS } from '../config/gameConstants';

const DB_NAME = 'phoneverse';
const DB_VERSION = DATABASE_CONSTANTS.version;

let db: IDBPDatabase | null = null;

export async function getDb(): Promise<IDBPDatabase> {
  if (!db) {
    db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        // Products store
        if (!database.objectStoreNames.contains('products')) {
          const productStore = database.createObjectStore('products', { keyPath: 'id' });
          productStore.createIndex('companyId', 'companyId');
          productStore.createIndex('status', 'status');
          productStore.createIndex('releaseDate', 'releaseDate');
        }

        // Designs store
        if (!database.objectStoreNames.contains('designs')) {
          const designStore = database.createObjectStore('designs', { keyPath: 'id' });
          designStore.createIndex('createdAt', 'createdAt');
          designStore.createIndex('techScore', 'techScore');
          designStore.createIndex('valueScore', 'valueScore');
        }

        // Production batches store
        if (!database.objectStoreNames.contains('production_batches')) {
          const batchStore = database.createObjectStore('production_batches', { keyPath: 'id' });
          batchStore.createIndex('productId', 'productId');
          batchStore.createIndex('status', 'status');
          batchStore.createIndex('startDate', 'startDate');
        }

        // Production metrics store
        if (!database.objectStoreNames.contains('production_metrics')) {
          const metricsStore = database.createObjectStore('production_metrics', { keyPath: 'id' });
          metricsStore.createIndex('productId', 'productId');
          metricsStore.createIndex('lastUpdated', 'lastUpdated');
        }

        // Quality reports store
        if (!database.objectStoreNames.contains('quality_reports')) {
          const reportsStore = database.createObjectStore('quality_reports', { keyPath: 'id' });
          reportsStore.createIndex('productId', 'productId');
          reportsStore.createIndex('batchId', 'batchId');
          reportsStore.createIndex('date', 'date');
        }

        // Component inventory store
        if (!database.objectStoreNames.contains('component_inventory')) {
          const componentInventoryStore = database.createObjectStore('component_inventory', { keyPath: 'id' });
          componentInventoryStore.createIndex('componentId', 'componentId');
          componentInventoryStore.createIndex('category', 'category');
          componentInventoryStore.createIndex('currentStock', 'currentStock');
        }

        // Device inventory store
        if (!database.objectStoreNames.contains('device_inventory')) {
          const deviceInventoryStore = database.createObjectStore('device_inventory', { keyPath: 'id' });
          deviceInventoryStore.createIndex('productId', 'productId');
          deviceInventoryStore.createIndex('currentStock', 'currentStock');
        }

        // Inventory transactions store
        if (!database.objectStoreNames.contains('inventory_transactions')) {
          const transactionsStore = database.createObjectStore('inventory_transactions', { keyPath: 'id' });
          transactionsStore.createIndex('type', 'type');
          transactionsStore.createIndex('itemType', 'itemType');
          transactionsStore.createIndex('itemId', 'itemId');
          transactionsStore.createIndex('date', 'date');
        }

        // Component orders store
        if (!database.objectStoreNames.contains('component_orders')) {
          const ordersStore = database.createObjectStore('component_orders', { keyPath: 'orderId' });
          ordersStore.createIndex('componentId', 'componentId');
          ordersStore.createIndex('status', 'status');
          ordersStore.createIndex('orderDate', 'orderDate');
          ordersStore.createIndex('expectedDeliveryDate', 'expectedDeliveryDate');
        }

        // Inventory alerts store
        if (!database.objectStoreNames.contains('inventory_alerts')) {
          const alertsStore = database.createObjectStore('inventory_alerts', { keyPath: 'id' });
          alertsStore.createIndex('type', 'type');
          alertsStore.createIndex('severity', 'severity');
          alertsStore.createIndex('itemType', 'itemType');
          alertsStore.createIndex('itemId', 'itemId');
          alertsStore.createIndex('date', 'date');
          alertsStore.createIndex('isResolved', 'isResolved');
        }

        // Persistence store for Zustand stores
        if (!database.objectStoreNames.contains('persistence')) {
          database.createObjectStore('persistence', { keyPath: 'id' });
        }

        // Migrations store for DataMigrationService
        if (!database.objectStoreNames.contains('migrations')) {
          const migrationsStore = database.createObjectStore('migrations', { keyPath: 'id' });
          migrationsStore.createIndex('type', 'type');
          migrationsStore.createIndex('dataType', 'dataType');
          migrationsStore.createIndex('timestamp', 'timestamp');
        }

        // Backups store for DataMigrationService
        if (!database.objectStoreNames.contains('backups')) {
          const backupsStore = database.createObjectStore('backups', { keyPath: 'id' });
          backupsStore.createIndex('dataType', 'dataType');
          backupsStore.createIndex('version', 'version');
          backupsStore.createIndex('timestamp', 'timestamp');
        }
      }
    });
  }
  return db;
}

export async function closeDb(): Promise<void> {
  if (db) {
    db.close();
    db = null;
  }
}

// Helper function to generate unique IDs
export function generateId(prefix: string = ''): string {
  return `${prefix}${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}